import { z } from "zod";

// D71 — Logic-System: der engine-NEUTRALE LogicGraph ist das GESPEICHERTE Format (SSOT), nicht das
// Blockly-interne. Blockly (D71) ist nur die austauschbare Oberfläche; der Interpreter (logic-runtime)
// führt diesen Graph über engine-spezifische Resolver-Ports aus. D51 gewahrt: der Browser baut nur
// Daten, die Ausführung ist die Engine-Runtime des Users.
//
// when.event / do.action sind BEHAVIOR-QUALIFIZIERT ⇒ die Bindung ist IMMER eindeutig (D70), auch
// wenn eine Entity mehrere Behaviors mit gleichlautenden event-/action-ids trägt. behavior = "global"
// für editor-typ-globale Vokabeln (on-start/on-collide/goto-level …, vom Editor D75 eingespeist).

/** Ziel-Referenz: eine konkrete Level-Instanz, das auslösende Objekt selbst, oder global. */
export const EntityRef = z.union([z.object({ iid: z.string().min(1) }), z.literal("self"), z.literal("global")]);
export type EntityRef = z.infer<typeof EntityRef>;

/** Ein Wert: Literal, gelesener State (D70 states) oder gelesene Prop (D70 props). */
export const Value = z.union([
  z.object({ lit: z.union([z.string(), z.number(), z.boolean()]) }),
  z.object({ state: z.object({ target: EntityRef, behavior: z.string().min(1), id: z.string().min(1) }) }),
  z.object({ prop: z.object({ target: EntityRef, behavior: z.string().min(1), name: z.string().min(1) }) }),
]);
export type Value = z.infer<typeof Value>;

export const Comparator = z.enum(["eq", "neq", "lt", "lte", "gt", "gte"]);
export type Comparator = z.infer<typeof Comparator>;

/** Rekursive Bedingung (and/or/not enthalten wieder Conditions). */
export type Condition =
  | { cmp: Comparator; left: Value; right: Value }
  | { and: Condition[] }
  | { or: Condition[] }
  | { not: Condition };

export const Condition: z.ZodType<Condition> = z.lazy(() =>
  z.union([
    z.object({ cmp: Comparator, left: Value, right: Value }),
    z.object({ and: z.array(Condition) }),
    z.object({ or: z.array(Condition) }),
    z.object({ not: Condition }),
  ]),
);

/** Eine behavior-qualifizierte Aktion (D70) — der ursprüngliche (und häufigste) `do`-Schritt.
 *  `.strict()` (Codex-P1): die Step-Union (s.u.) ist nur disjunkt, wenn KEIN Arm fremde Keys strippt —
 *  ein Mischling `{target, behavior, action, wait: …}` würde sonst still als Aktion geparst und das
 *  `wait` verlöre sich lautlos. Mit strikten Armen failt so ein Objekt ALLE Arme (fail-closed). */
export const LogicAction = z
  .object({
    target: EntityRef,
    behavior: z.string().min(1),
    action: z.string().min(1),
    params: z.record(z.string(), Value).optional(),
  })
  .strict();
export type LogicAction = z.infer<typeof LogicAction>;

/**
 * D105 — ein Schritt der `do`-Sequenz: Aktion ODER Ablauf-Kontrolle (warten/wiederholen/verzweigen).
 * Die Aktion bleibt der ERSTE Union-Arm in unveränderter Form ⇒ bestehende Graphen (reine
 * Aktions-Arrays) parsen unverändert (abwärtskompatibel, kein Migrationsbedarf). Rekursiv: repeat/
 * branch tragen verschachtelte Schritt-Sequenzen. `wait.seconds`/`repeat.times` sind `Value`s und
 * werden ZUR SCHRITT-ZEIT evaluiert (nach einem `wait` zählt der dann aktuelle Zustand).
 */
export type LogicStep =
  | LogicAction
  | { wait: { seconds: Value } }
  | { repeat: { times: Value; do: LogicStep[] } }
  | { branch: { if: Condition; then: LogicStep[]; else?: LogicStep[] } };

export const LogicStep: z.ZodType<LogicStep> = z.lazy(() =>
  z.union([
    LogicAction,
    // alle Arme strikt (Codex-P1) — ambige Mischlinge failen die GESAMTE Union statt still zu strippen.
    z.object({ wait: z.object({ seconds: Value }) }).strict(),
    // verschachtelte Sequenzen dürfen LEER sein (WIP-freundlich); nur das Top-Level-`do` hat min(1).
    z.object({ repeat: z.object({ times: Value, do: z.array(LogicStep) }) }).strict(),
    z.object({ branch: z.object({ if: Condition, then: z.array(LogicStep), else: z.array(LogicStep).optional() }) }).strict(),
  ]),
);

/** Ein „Wenn → (falls) → Dann"-Regel. */
export const LogicRule = z.object({
  id: z.string().min(1),
  when: z.union([
    // behavior-qualifiziertes Event (D70) ⇒ eindeutig.
    z.object({ target: EntityRef, behavior: z.string().min(1), event: z.string().min(1) }),
    // Zustands-Änderung als Auslöser (z.B. Schalter wird true).
    z.object({
      stateChange: z.object({
        target: EntityRef,
        behavior: z.string().min(1),
        id: z.string().min(1),
        to: Value,
      }),
    }),
  ]),
  if: Condition.optional(),
  // Pflicht + min. 1 Schritt: eine Regel ohne `do` ist tote Konfiguration (Editor_PRD §9).
  do: z.array(LogicStep).min(1),
});
export type LogicRule = z.infer<typeof LogicRule>;

export const LogicGraph = z.object({ rules: z.array(LogicRule).default([]) });
export type LogicGraph = z.infer<typeof LogicGraph>;
